import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import {
  RowSelectionContext,
  RowSelectionStore,
} from "@/components/data-table/context/RowSelectionContext";
import { QueryTable } from "@/components/query-table/QueryTable";
import { useProjectStoresQuery } from "@/modules/projects/hooks/useProjectStoresQuery";
import { memo, useMemo } from "react";
import { DisplayProjectStore } from "@/types/projectStore";
import { displayProjectStore } from "@/schemas/projectStore";
import { useTranslation } from "react-i18next";
import { FilterType } from "@/lib/filters";

interface StoreProjectAssigneesCreatorStoresProps {
  projectId: string;
  store: RowSelectionStore;
}

export const StoreProjectAssigneesCreatorStores = memo(
  ({ projectId, store }: StoreProjectAssigneesCreatorStoresProps) => {
    const { t } = useTranslation();
    const contextValue = useMemo(() => ({ store }), [store]);

    const filters = useMemo(
      () =>
        projectId
          ? [
              {
                id: "project_id",
                value: { value: projectId, filter: FilterType.EqualsString },
              },
            ]
          : [],
      [projectId],
    );
    const { query: projectStoresQuery, options: projectStoresOptions } =
      useProjectStoresQuery({
        projectId,
        name: "storeProjectAssigneesCreatorStores",
        enabled: Boolean(projectId),
        filters,
      });

    if (!projectId) {
      return (
        <p className="text-sm text-muted-foreground py-4">
          {t("storeProjectAssignees.title.stores")} –{" "}
          {t("storeProjectAssignees.creator.selectProjectFirst")}
        </p>
      );
    }

    return (
      <RowSelectionContext.Provider value={contextValue}>
        <DataTableQueryContextProvider value={{ query: projectStoresQuery }}>
          <QueryTable<DisplayProjectStore>
            schema={displayProjectStore}
            name="storeProjectAssigneesCreatorStores"
            translationKey="projectStores"
            query={projectStoresQuery}
            options={projectStoresOptions}
            tableFeatures={{
              enableRowSelection: true,
              enableSelectAll: true,
            }}
          >
            <div className="flex flex-row items-center gap-2">
              <QueryTable.Menubar className="grow">
                <QueryTable.Menubar.Columns />
                <QueryTable.Menubar.Settings />
              </QueryTable.Menubar>
            </div>
            <div className="flex flex-row grow gap-2 overflow-hidden">
              <QueryTable.Content className="overflow-x-auto flex-grow">
                <QueryTable.Status>
                  <QueryTable.SelectedRowsStatus />
                </QueryTable.Status>
              </QueryTable.Content>
            </div>
          </QueryTable>
        </DataTableQueryContextProvider>
      </RowSelectionContext.Provider>
    );
  },
);
